import { KeyboardArrowDown } from "@mui/icons-material";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import type { MouseEvent } from "react";
import { useCallback, useId, useState } from "react";
import {
  IMPACT_FILTER_TRIGGER_ATTRIBUTE,
  IMPACT_REPORT_BAR_COMPACT_MAX_PX,
  IMPACT_REPORT_BAR_NARROW_MAX_PX,
} from "@/constants/impactReport";
import { useImpactMenuCoordinator } from "@/context/ImpactMenuCoordinatorContext";
import type { ImpactFilterMenuProps } from "@/interfaces/impactReport";

/** The prototype's `.ir-ddbtn`: a 44px control that reads as a word, not a form field. */
const CONTROL_HEIGHT = "44px";

/**
 * THE SAME BREAKPOINT `useIsMobile()` USES (`hooks/useMediaQuery.ts`), written as CSS rather than
 * read through the hook: the control has to be narrow at narrow widths, which is a paint decision,
 * not a state decision — a media query gets it right on first paint and on every resize without a
 * re-render, and cannot disagree with the hook because it is the identical query.
 *
 * Below it the button COLLAPSES TO ITS ICON. Four controls at their full width (icon + uppercase
 * word + chevron ≈ 120px each) total ~500px, which does not fit a 375px viewport once the mobile
 * hamburger and the toolbar's own padding are taken out; the icon-only form is ~44px, so all four
 * fit — CAMPAIGN and DATE included, and CAMPAIGN is the only control that can produce a scope, so
 * a user who cannot reach it cannot run the report at all. The button keeps its `aria-label` and
 * gains a `title`, so the collapsed form is still named for assistive tech and on hover, and the
 * 44px square keeps the touch target at the recommended size.
 */
// Keyed on the shared threshold, so the controls' icon-only form and the pills' collapse can
// never disagree about where the bar stops fitting.
const MOBILE_MEDIA_QUERY = `@media (max-width: ${IMPACT_REPORT_BAR_COMPACT_MAX_PX}px)`;

/**
 * A THIRD tier, below the icon-only one, holding the 320px floor — WCAG 2.1 SC 1.4.10 (Reflow)
 * requires content to work at 320 CSS px, which is also a 1280px desktop at 400% zoom, so it
 * serves low-vision users on desktop as much as it does small phones.
 *
 * The BUTTON tightens here; the icon does not change. In the compact form each control is a 44px square, and
 * four of them plus their gaps, the drawer toggle and the collapsed filter pill come to roughly
 * 323px — a few pixels over a 320px bar. Trimming the horizontal padding brings each square to
 * 40px and buys back the difference. The 44px HEIGHT is deliberately untouched, so the tap target
 * stays comfortable vertically and well clear of the 24px AA floor.
 */
const NARROW_MEDIA_QUERY = `@media (max-width: ${IMPACT_REPORT_BAR_NARROW_MAX_PX}px)`;

/**
 * The control bar's one dropdown primitive — an icon plus one uppercase word, and an anchored
 * menu. All four controls (VIEW, CAUSE, CAMPAIGN, DATE) are this component, so they cannot drift
 * apart in geometry, anchoring or keyboard behaviour. VIEW, CAUSE and DATE pass menu children;
 * CAMPAIGN passes `onTriggerClick` instead and opens its picker dialog, so it renders no `Menu`.
 *
 * Built on MUI's `Menu`, like `BlotterFilterBar.tsx`: the positioning, the focus trap, the
 * outside-click dismissal and the Escape handling are all MUI's, and hand-rolling any of them
 * here would be re-solving a problem the repo already has one answer to.
 *
 * RIGHT-ANCHORED, mirroring the prototype's `.ir-menu`. The controls sit at the bar's right edge,
 * so a left-anchored menu wider than its button would open off-screen.
 *
 * `isActive` lights the button up when its filter is at a NON-DEFAULT value — the bar's only
 * at-a-glance answer to "is this report narrowed?". VIEW passes `false` permanently: it always
 * has a value, so an "active" VIEW would be lit on every render and mean nothing.
 *
 * ── SINGLE-OPEN COORDINATION, AND WHY THE BACKDROP NEEDS A HANDLER ──────────────────────────────
 * MUI's `Menu` renders a modal backdrop over the whole viewport while open — that is what makes
 * "tap elsewhere to dismiss" work at all. But it also means a tap on a DIFFERENT control's trigger
 * lands on THIS menu's backdrop first: the backdrop dismisses this menu and swallows the click,
 * so the other control never opens. That is the double-tap bug this component fixes, in two
 * halves that only work together:
 *
 * 1. `useImpactMenuCoordinator()` — when present (mounted by `ImpactControlBar` around the whole
 *    bar) — holds ONE shared "which instance is open" id instead of each instance owning its
 *    `anchorEl` privately. `isOpen` is derived from it every render, so when a different instance
 *    claims the slot this one closes in the SAME commit, not on a later effect tick.
 * 2. The backdrop's `onClick` (below) peeks at what DOM node is actually under the click — by
 *    briefly making the backdrop itself `pointer-events: none` and asking
 *    `document.elementFromPoint` — and, if it is another control's trigger
 *    (`IMPACT_FILTER_TRIGGER_ATTRIBUTE`), forwards the click by calling `.click()` on it. That
 *    trigger's own `onClick` then claims the coordinator slot, closing this menu and opening the
 *    other one from a SINGLE tap.
 *
 * Neither half alone is enough: coordination without the forwarded click still eats the first tap
 * (the backdrop swallows it before it ever reaches the other button); the forwarded click without
 * coordination can leave two menus open if the newly-opened one does not also know to close this
 * one. Genuine outside clicks (the page body, anything that is not a registered trigger) fall
 * through untouched to MUI's own `backdropClick` handling, which still fires after this handler
 * regardless — see the coordinator's `closeMenu` for why that is safe even when this handler has
 * already switched the open slot to another instance.
 *
 * `pointer-events: none` on the backdrop was considered and rejected: MUI has no OTHER listener
 * for "the user tapped empty page", so a permanently click-through backdrop would silently break
 * dismiss-on-outside-click for every menu on this bar, trading a double-tap bug for a menu that
 * never closes on its own.
 */
export function ImpactFilterMenu({
  label,
  icon,
  iconOpticalScale = 1,
  isActive,
  buttonAriaLabel,
  menuMinWidth = 250,
  menuMaxHeight,
  triggerButtonRef,
  renderTrigger,
  onTriggerClick,
  children,
}: ImpactFilterMenuProps) {
  // Stable per mounted instance — the coordinator's shared slot is keyed on this, never on a
  // control name, so it has no idea whether it is holding VIEW, CAUSE, CAMPAIGN, DATE or the
  // collapsed pill.
  const instanceId = useId();
  const coordinator = useImpactMenuCoordinator();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Derived, not stored: with a coordinator present, another instance claiming the shared slot
  // closes this one in the SAME render as the claim, with no separate effect/tick where both (or
  // neither) could appear open. Without a coordinator (this component mounted standalone,
  // uncoordinated), `anchorEl` alone decides — today's original behaviour.
  const isOpen =
    anchorEl !== null && (coordinator === null || coordinator.openMenuId === instanceId);

  // Handed to `children` so each control decides for itself whether a click closes the menu. VIEW
  // and the DATE presets close; CAUSE deliberately does not, so several values can be bunched onto
  // one report without reopening the menu between each.
  const closeMenu = useCallback(() => {
    setAnchorEl(null);
    coordinator?.closeMenu(instanceId);
  }, [coordinator, instanceId]);

  // Handed to `renderTrigger` (and wired to the default button below either way) so a caller-
  // supplied trigger opens the SAME anchor state as the built-in one, without reaching into it.
  const openMenu = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      coordinator?.openMenu(instanceId);
    },
    [coordinator, instanceId],
  );

  // Claims and releases the shared slot in one batch: whichever menu held it closes in this same
  // commit, exactly as when another menu opens, and nothing is left holding the slot — there is no
  // `Menu` here whose `onClose` would ever release it.
  const handleTriggerClick = useCallback(() => {
    coordinator?.openMenu(instanceId);
    coordinator?.closeMenu(instanceId);
    onTriggerClick?.();
  }, [coordinator, instanceId, onTriggerClick]);

  // See the file-level comment's second half. Only relevant with a coordinator mounted — with
  // none, there is nothing else on the bar to forward to.
  const handleBackdropClick = useCallback((event: MouseEvent<HTMLElement>) => {
    const backdropEl = event.currentTarget;
    // Peek at what the click would have hit had the backdrop not been there: make it transparent
    // to hit-testing for exactly one synchronous lookup, then restore it immediately. There is no
    // paint between these two lines — the backdrop never visibly loses its click-catching role.
    backdropEl.style.pointerEvents = "none";
    const elementBelow = document.elementFromPoint(event.clientX, event.clientY);
    backdropEl.style.pointerEvents = "";

    const trigger = elementBelow?.closest<HTMLElement>(`[${IMPACT_FILTER_TRIGGER_ATTRIBUTE}]`);
    // Not a trigger (empty page, or some other control): do nothing extra. MUI's own
    // `backdropClick` handling still runs after this and closes this menu normally — that is the
    // genuine "outside click" case this deliberately leaves alone.
    trigger?.click();
  }, []);

  return (
    <>
      {renderTrigger ? (
        renderTrigger({
          onOpen: openMenu,
          isOpen,
          triggerAttributes: { [IMPACT_FILTER_TRIGGER_ATTRIBUTE]: "true" },
        })
      ) : (
        <Box
          component="button"
          type="button"
          ref={triggerButtonRef}
          onClick={onTriggerClick === undefined ? openMenu : handleTriggerClick}
          {...{ [IMPACT_FILTER_TRIGGER_ATTRIBUTE]: "true" }}
          aria-haspopup={onTriggerClick === undefined ? "menu" : "dialog"}
          aria-expanded={isOpen}
          aria-label={buttonAriaLabel ?? label}
          // Names the collapsed, icon-only form on hover. Harmless at full width, where the word is
          // already on the button.
          title={buttonAriaLabel ?? label}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            height: CONTROL_HEIGHT,
            px: "18px",
            flexShrink: 0,
            [MOBILE_MEDIA_QUERY]: {
              // 44px square: the icon plus symmetrical padding, once the word and the chevron below
              // are out of the flow.
              gap: 0,
              px: "12px",
              // The icon carries the control on its own here, so it grows to suit the 44px square.
              // At full width 18px is balanced against the word beside it; alone it reads undersized,
              // and it is also the whole touch affordance rather than a decoration next to a label.
              "& .MuiSvgIcon-root": { fontSize: 22 * iconOpticalScale },
            },
            // AFTER the mobile block on purpose: both queries match at 320px, and in an sx object
            // the later declaration wins — placed above, mobile's `px` would override this.
            [NARROW_MEDIA_QUERY]: {
              // See `NARROW_MEDIA_QUERY`: the square narrows to 40px, the icon is left alone.
              px: "10px",
            },
            whiteSpace: "nowrap",
            borderRadius: "8px",
            border: "1px solid",
            // The prototype's `.ir-ddbtn.active`: a darker border over light dust, never a colour
            // change on the label — the word has to stay legible in both states.
            borderColor: isActive ? "var(--color-dark-dust)" : "var(--color-smoke)",
            bgcolor: isActive ? "var(--color-light-dust)" : "var(--color-white)",
            fontFamily: "var(--font-lato)",
            fontSize: "13px",
            fontWeight: 400,
            textTransform: "uppercase",
            letterSpacing: "1px",
            color: "var(--color-rodeo-black)",
            cursor: "pointer",
            transition: "border-color 0.15s, background 0.15s",
            "&:hover": { borderColor: "var(--color-dark-dust)" },
            "&:focus-visible": {
              outline: "2px solid var(--color-primary-active)",
              outlineOffset: "2px",
            },
            "& .MuiSvgIcon-root": {
              fontSize: 18 * iconOpticalScale,
              color: "var(--color-dark-dust)",
            },
          }}
        >
          {icon}
          {/* The word and the chevron are what make the control ~120px wide, and they are the parts
              a 375px bar cannot afford. Hidden — not removed — below the mobile breakpoint: the
              button's own `aria-label`/`title` carry the name in the collapsed form, and
              `aria-haspopup` above still announces that it opens a menu or dialog. */}
          <Box component="span" sx={{ [MOBILE_MEDIA_QUERY]: { display: "none" } }}>
            {label}
          </Box>
          <KeyboardArrowDown sx={{ [MOBILE_MEDIA_QUERY]: { display: "none" } }} />
        </Box>
      )}

      {onTriggerClick === undefined && (
        <Menu
          anchorEl={anchorEl}
          open={isOpen}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              sx: {
                mt: "6px",
                // `min()` rather than a bare number: a CSS `min-width` BEATS `max-width`, so a 320px
                // menu on a 375px-or-narrower screen would push itself past the viewport edge — the
                // same unreachability the collapsed button above exists to prevent.
                minWidth: `min(${menuMinWidth}px, calc(100vw - 32px))`,
                maxHeight: menuMaxHeight,
                borderRadius: "10px",
                border: "1px solid var(--color-smoke)",
                boxShadow: "0 8px 28px rgba(0, 0, 0, 0.12)",
              },
            },
            // See the file-level comment: this is the click-forwarding half of the single-open fix.
            // MUI still runs its own `backdropClick` → `onClose` handling AFTER this fires (that is
            // internal to `Modal` and not something this prop can opt out of), so a genuine outside
            // click closes exactly as it always has.
            backdrop: { onClick: handleBackdropClick },
          }}
        >
          {children(closeMenu)}
        </Menu>
      )}
    </>
  );
}
